import React from "react";
import { Edit, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import { fetchGarmentTypes, saveGarmentType, deleteGarmentType } from "@/lib/api/manufacturing";

export function GarmentTypeManager() {
  const [types, setTypes] = React.useState([]);
  const [search, setSearch] = React.useState("");
  const [editor, setEditor] = React.useState(null);
  const [name, setName] = React.useState("");
  const [deleting, setDeleting] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const { toast } = useToast();
  const load = React.useCallback(async () => {
    setLoading(true);
    const result = await fetchGarmentTypes();
    if (result.error) toast({ title: "Error", description: "No se pudieron cargar los tipos de prenda", variant: "destructive" });
    else setTypes(result.data);
    setLoading(false);
  }, [toast]);
  React.useEffect(() => { load(); }, [load]);
  const open = (type = {}) => { setEditor(type); setName(type.name || ""); };
  const mutate = async (operation, close) => {
    if (busy) return;
    setBusy(true);
    try {
      const result = await operation();
      if (result.error) throw result.error;
      close();
      await load();
    } catch (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally { setBusy(false); }
  };
  const visible = types.filter((type) => type.name.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()));
  return <div className="space-y-4 rounded-lg border bg-white p-4 shadow-sm">
    <div className="flex flex-wrap items-center gap-3">
      <Input className="max-w-sm" aria-label="Buscar tipos de prenda" placeholder="Buscar tipos de prenda..." value={search} onChange={(event) => setSearch(event.target.value)} />
      <Button onClick={() => open()}><Plus className="mr-2 h-4 w-4" />Nuevo tipo de prenda</Button>
    </div>
    {loading ? <p>Cargando tipos de prenda...</p> : visible.length === 0 ? <p className="text-sm text-gray-500">No hay tipos de prenda.</p> :
      <div className="divide-y">{visible.map((type) => <div key={type.id} className="flex items-center justify-between gap-3 py-3">
        <span>{type.name}</span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" aria-label={`Editar ${type.name}`} onClick={() => open(type)}><Edit className="h-4 w-4" /></Button>
          <Button variant="destructive" size="sm" aria-label={`Eliminar ${type.name}`} onClick={() => setDeleting(type)}><Trash2 className="h-4 w-4" /></Button>
        </div>
      </div>)}</div>}
    <Dialog open={Boolean(editor)} onOpenChange={(value) => !value && !busy && setEditor(null)}>
      <DialogContent>
        <DialogHeader><DialogTitle>{editor?.id ? "Editar tipo de prenda" : "Nuevo tipo de prenda"}</DialogTitle><DialogDescription>Indica el nombre del tipo, por ejemplo: pantalones, camisas o vestidos.</DialogDescription></DialogHeader>
        <form className="space-y-4" onSubmit={(event) => { event.preventDefault(); mutate(() => saveGarmentType(name, editor.id), () => setEditor(null)); }}>
          <label className="block space-y-1 text-sm"><span>Nombre</span><Input required maxLength={100} value={name} disabled={busy} onChange={(event) => setName(event.target.value)} /></label>
          <div className="flex justify-end gap-2"><Button type="button" variant="outline" disabled={busy} onClick={() => setEditor(null)}>Cancelar</Button><Button disabled={busy || !name.trim()}>{busy ? "Guardando..." : "Guardar tipo de prenda"}</Button></div>
        </form>
      </DialogContent>
    </Dialog>
    <Dialog open={Boolean(deleting)} onOpenChange={(value) => !value && !busy && setDeleting(null)}>
      <DialogContent>
        <DialogHeader><DialogTitle>Eliminar tipo de prenda</DialogTitle><DialogDescription>¿Eliminar «{deleting?.name}»? Solo se puede eliminar si no está asignado a ningún artículo.</DialogDescription></DialogHeader>
        <div className="flex justify-end gap-2"><Button variant="outline" disabled={busy} onClick={() => setDeleting(null)}>Cancelar</Button><Button variant="destructive" disabled={busy} onClick={() => mutate(() => deleteGarmentType(deleting.id), () => setDeleting(null))}>{busy ? "Eliminando..." : "Eliminar"}</Button></div>
      </DialogContent>
    </Dialog>
  </div>;
}
